<template>
  <b-modal
    ref="centersModal"
    size="md"
    centered
    modal-class="centers-configuration-modal"
    @hidden="hide"
  >
    <div slot="modal-title">
      {{ actionTitle }} <span style="text-transform: lowercase">{{ $tc('configuration.sections.center', 1) }}</span>
    </div>
    <b-row>
      <b-col>
        <div class="form-card">
          <span class="ap-title ap-title__section"><i
            class="far fa-hospital mr-2 mb-2"
            aria-hidden="true"
          />{{ $t('configuration.modal.center.centerData') }}</span>
          <b-row class="mt-2">
            <b-col
              cols="3"
              class="mb-3"
            >
              <div class="form-label">
                <label>{{ $t('configuration.grid.externalCode') }} *</label>
              </div>
              <vt-input
                v-model="internalCenter.code"
                v-validate="'required'"
                :class="{ requiredForm: (showErrors && !internalCenter.code) }"
                :name="$t('configuration.grid.externalCode')"
                class="form-control-sm"
              />
            </b-col>
            <b-col
              cols="9"
              class="mb-3"
            >
              <div class="form-label">
                <label>{{ $tc('configuration.sections.center', 1) }} *</label>
              </div>
              <vt-input
                v-model="internalCenter.name"
                v-validate="'required'"
                :class="{ requiredForm: (showErrors && !internalCenter.name) }"
                :name="$t('configuration.sections.center')"
                class="form-control-sm"
              />
            </b-col>
            <b-col
              cols="6"
              class="mb-3"
            >
              <div class="form-label">
                <label>{{ $tc('configuration.sections.healthManagementArea', 1) }}</label>
              </div>
              <vt-combo-select
                v-model="internalCenter.healthManagementArea"
                :option-loader="getHealthManagementAreasByName"
                label="name"
                ajax-search
              />
            </b-col>
            <b-col
              cols="6"
              class="mb-3"
            >
              <div class="form-label">
                <label>{{ $t('configuration.modal.center.reportsLanguage') }} *</label>
              </div>
              <vt-combo-select
                v-model="internalCenter.language"
                v-validate="'required'"
                :class="{ requiredForm: (showErrors && !internalCenter.language) }"
                label="name"
                :options="languages"
              />
            </b-col>
          </b-row>
        </div>
      </b-col>
    </b-row>
    <b-row class="mt-3">
      <b-col>
        <div class="form-card">
          <span class="ap-title ap-title__section"><i
            class="far fa-map-marker-alt mr-3"
            aria-hidden="true"
          />{{ $t('configuration.identitySection.location') }}</span>
          <b-row class="mt-2">
            <b-col
              cols="9"
              class="mb-3"
            >
              <div>
                <div class="form-label">
                  <label>{{ $t('configuration.identitySection.address') }} *</label>
                </div>
                <vt-input
                  v-model="internalCenter.address.address"
                  v-validate="'required'"
                  :class="{ requiredForm: (showErrors && !internalCenter.address.address) }"
                  :name="$t('configuration.identitySection.address')"
                  class="form-control-sm"
                />
              </div>
            </b-col>
            <b-col
              cols="3"
              class="mb-3"
            >
              <div>
                <div class="form-label">
                  <label>{{ $t('configuration.identitySection.postalCode') }} *</label>
                </div>
                <vt-input
                  v-model="internalCenter.address.postalCode"
                  v-validate="'required'"
                  :class="{ requiredForm: (showErrors && !internalCenter.address.postalCode) }"
                  :name="$t('configuration.identitySection.postalCode')"
                  class="form-control-sm"
                />
              </div>
            </b-col>
            <b-col
              cols="4"
              class="mb-3"
            >
              <div>
                <div class="form-label">
                  <label>{{ $t('configuration.identitySection.country') }} *</label>
                </div>
                <vt-combo-select
                  v-model="internalCenter.address.country"
                  v-validate="'required'"
                  :class="{ requiredForm: (showErrors && !internalCenter.address.country) }"
                  label="name"
                  :option-loader="loadCountries"
                  @input="onSelectCountry"
                />
              </div>
            </b-col>
            <b-col
              cols="4"
              class="mb-3"
            >
              <div>
                <div class="form-label">
                  <label>{{ $t('configuration.identitySection.province') }} *</label>
                </div>
                <vt-combo-select
                  v-model="internalCenter.address.province"
                  v-validate="'required'"
                  :class="{ requiredForm: (showErrors && !internalCenter.address.province) }"
                  label="name"
                  :disabled="!internalCenter.address.country"
                  :options="provinces"
                  @input="onSelectProvince"
                />
              </div>
            </b-col>
            <b-col
              cols="4"
              class="mb-3"
            >
              <div>
                <div class="form-label">
                  <label>{{ $t('configuration.identitySection.municipality') }} *</label>
                </div>
                <vt-combo-select
                  v-model="internalCenter.address.municipality"
                  v-validate="'required'"
                  :class="{ requiredForm: (showErrors && !internalCenter.address.municipality) }"
                  label="name"
                  :disabled="!internalCenter.address.province"
                  :options="municipalities"
                  @input="$forceUpdate()"
                />
              </div>
            </b-col>
          </b-row>
        </div>
      </b-col>
    </b-row>
    <b-row class="mt-3">
      <b-col>
        <div class="form-card">
          <span class="ap-title ap-title__section"><i
            class="far fa-envelope mr-2 mb-2"
            aria-hidden="true"
          />{{ $t('configuration.modal.contactAndNotifications') }}</span>
          <b-row class="mt-2">
            <b-col
              cols="6"
              class="mb-3"
            >
              <div class="form-label">
                <label>{{ $t('configuration.grid.email') }} *</label>
              </div>
              <vt-input
                v-model="internalCenter.contactInformation.email"
                v-validate="'required'"
                :class="{ requiredForm: (showErrors && !internalCenter.contactInformation.email) }"
                :name="$t('configuration.grid.email')"
                class="form-control-sm"
              />
            </b-col>
            <b-col
              cols="6"
              class="mb-3 d-flex align-items-center justify-content-end"
            >
              <div class="switch-btn mt-1 p-0 d-flex align-items-center">
                <span class="mr-2">{{ $t('configuration.modal.emailNotifications') }}</span>
                <input
                  id="toggle-email"
                  v-model="internalCenter.contactInformation.emailNotificationEnabled"
                  :disabled="!internalCenter.contactInformation.email"
                  type="checkbox"
                  class="offscreen"
                  aria-hidden="true"
                >
                <label
                  for="toggle-email"
                  class="switch mb-0"
                />
              </div>
            </b-col>
          </b-row>
          <b-row>
            <b-col
              cols="6"
              class="mb-3"
            >
              <div class="form-label">
                <label>{{ $t('configuration.grid.phone') }} *</label>
              </div>
              <vt-input
                v-model="internalCenter.contactInformation.mainPhone"
                v-validate="'required'"
                :class="{ requiredForm: (showErrors && !internalCenter.contactInformation.mainPhone) }"
                :name="$t('configuration.grid.phone')"
                class="form-control-sm"
              />
            </b-col>
            <b-col
              cols="6"
              class="mb-3 d-flex align-items-center justify-content-end"
            >
              <div class="switch-btn mt-1 p-0 d-flex align-items-center">
                <span class="mr-2">{{ $t('configuration.modal.smsNotifications') }}</span>
                <input
                  id="toggle-sms"
                  v-model="internalCenter.contactInformation.smsNotificationEnabled"
                  :disabled="!internalCenter.contactInformation.mainPhone"
                  type="checkbox"
                  class="offscreen"
                  aria-hidden="true"
                >
                <label
                  for="toggle-sms"
                  class="switch mb-0"
                />
              </div>
            </b-col>
          </b-row>
        </div>
      </b-col>
    </b-row>
    <b-row>
      <b-col
        cols="12"
        class="my-2 d-flex justify-content-end"
      >
        <div class="switch-btn mt-1 p-0 d-flex align-items-center">
          <span class="mr-2">{{ $t('configuration.actions.active') }}</span>
          <input
            id="toggle"
            v-model="internalCenter.active"
            type="checkbox"
            class="offscreen"
            aria-hidden="true"
          >
          <label
            for="toggle"
            class="switch mb-0"
          />
        </div>
      </b-col>
    </b-row>
    <div
      slot="modal-footer"
      class="footer-banner px-2"
      style="width:100%;"
    >
      <div class="row">
        <div class="col-6 text-left">
          <b-button
            size="sm"
            variant="outline-secondary"
            data-dismiss="modal"
            @click="hide"
          >
            {{ $t('global.cancel') }}
          </b-button>
        </div>
        <div class="col-6 text-right">
          <b-button
            size="sm"
            class="btn--primary"
            @click="update()"
          >
            <i
              class="fal fa-check mr-2"
              aria-hidden="true"
            />
            {{ actionTitle }}
          </b-button>
        </div>
      </div>
    </div>
  </b-modal>
</template>

<script>
import { cloneDeep, isEmpty } from 'lodash'
import ConfigurationHealthManagementAreaServices
    from '@/api/resources/configuration/centersAndAreas/healthManagementArea/ConfigurationHealthManagementAreaServices'
import languageService from '@/api/resources/common/languageService'
import countryResource from '@/api/resources/CountryResource'
import provinceResource from '@/api/resources/ProvinceResource'
import municipalityResource from '@/api/resources/MunicipalityResource'
import { GridUtils } from '@/shared/util/grid-utils'

export default {
    name: 'CentersModal',
    props: {
        actionTitle: String
    },
    data() {
        return {
            showErrors: false,
            selectedHealthManagementArea: null,
            selectedReportingLanguage: null,
            languages: [],
            provinces: [],
            municipalities: [],
            internalCenter: {
                active: true,
                contactInformation: {},
                address: {}
            }
        }
    },
    methods: {
        async show(selectedCenter) {
            if (selectedCenter && !isEmpty(selectedCenter)) {
                this.internalCenter = cloneDeep(selectedCenter)
                this.provinces = await this.loadProvinces(this.internalCenter.address.country.id)
                this.municipalities = await this.loadMunicipalities(this.internalCenter.address.province.id)
            }
            this.languages = await this.findAllActiveAndUiEnabled()
            this.$refs.centersModal.show()
        },
        hide() {
            this.$refs.centersModal.hide()
            this.clearData()
        },
        async update() {
            const validForm = await this.$validator.validate()
            if (!validForm) {
                this.showErrors = true
                this.showErrorModalGlobal(this.$t('studyRequest.general.requiredFieldsNotice'))
                return false
            } else {
                if (this.internalCenter.id) this.$emit('update', this.internalCenter)
                else this.$emit('create', this.internalCenter)
            }
        },
        async getHealthManagementAreasByName(name) {
            const filterModel = {
                'name': GridUtils.getTextFilterModel('contains', name ?? '')
            }
            const response = await ConfigurationHealthManagementAreaServices.paginator(filterModel)
            return response.content
        },
        async onSelectCountry(country) {
            this.provinces = await this.loadProvinces(country.id)
            this.internalCenter.address.province = null
            this.internalCenter.address.municipality = null
        },
        async onSelectProvince(province) {
            this.municipalities = await this.loadMunicipalities(province.id)
            this.internalCenter.address.municipality = null
        },
        async loadCountries() {
            return countryResource.index()
        },
        async loadProvinces(countryId) {
            return provinceResource.index(countryId)
        },
        async loadMunicipalities(provinceId) {
            return municipalityResource.index(provinceId)
        },
        findAllActiveAndUiEnabled() {
            return languageService.findAllActiveAndUiEnabled()
        },
        clearData() {
            this.$_cleanComponentData()
        }
    }
}
</script>

<style lang="scss">
    .requiredForm {
        border-color: red;
    }
    .requiredForm .multiselect__tags {
        border-color: red;
    }
</style>
